<template>
  <v-form ref="form">
    <v-dialog
      v-model="dialog"
      persistent
      max-width="512"
    >
      <v-card>
        <v-card-title class="headline px-6 py-4">{{ $i18n.t('settings.addingWMS') }}</v-card-title>
        <v-card-text class="py-0">
          <v-row>
            <v-col
              xs="12"
              class="py-0"
            >
              <v-text-field
                outlined
                v-model="wmsData.wms_url"
                :label="$i18n.t('settings.serverAddres')"
                :error-messages="error ? $i18n.t('settings.wmsErrorMessage') : ''"
                :rules="[rules.required]"
              >
                <template v-slot:append-outer>
                  <v-icon
                    @click="downloadLayers"
                    :title="$i18n.t('settings.downloadLayers')"
                    :disabled="downloadingCapabilities"
                  >mdi-download-outline</v-icon>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <template v-if="isParsed">
            <v-row>
              <v-col
                xs="12"
                class="py-0"
              >
                <v-text-field
                  outlined
                  v-model="wmsData.name_pl"
                  :label="$i18n.t('default.titlePL')"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                xs="12"
                class="py-0"
              >
                <v-text-field
                  outlined
                  v-model="wmsData.name_en"
                  :label="$i18n.t('default.titleEN')"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col class="py-0">
                <v-select
                  :attach="true"
                  v-model="wmsData.wms_srs"
                  :items="coordsSystems"
                  :label="$i18n.t('settings.coordSystem')"
                  :rules="[rules.required]"
                  outlined
                  :no-data-text="$i18n.t('default.noItems')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-0">
                <v-select
                  :attach="true"
                  v-model="wmsData.wms_layers"
                  :items="layers"
                  :label="$i18n.t('settings.layers')"
                  item-text="Title"
                  item-value="Name"
                  multiple
                  outlined
                  :no-data-text="$i18n.t('default.noItems')"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-0">
                <v-subheader class="pl-0">{{ $i18n.t('settings.formats') }}</v-subheader>
                <v-radio-group
                  v-model="wmsData.wms_format"
                  row
                  :mandatory="true"
                  class="mt-0"
                >
                  <v-radio
                    v-for="format in formats"
                    :key="format"
                    :label="format | formatFilter"
                    :value="format"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1" />
          <v-btn
            color="primary"
            text
            @click="cancelDelete"
          >{{ $i18n.t('default.cancel') }}</v-btn>
          <v-btn
            v-if="isParsed"
            color="primary"
            text
            @click="confirmSuccess"
          >{{ $i18n.t('default.add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import WMSCapabilities from 'ol/format/WMSCapabilities';

export default {
  props: {
    dialog: {
      required: true,
      type: Boolean
    }
  },
  filters: {
    formatFilter(value) {
      return value.split('/')[1];
    }
  },
  data: () => ({
    error: false,
    coordsSystems: [],
    wmsData: {
      wms_format: undefined,
      wms_srs: undefined,
      wms_layers: [],
      name_pl: '',
      name_en: '',
      wms_url: ''
    },
    layers: [],
    formats: [],
    isParsed: false,
    downloadingCapabilities: false
  }),
  computed: {
    rules() {
      return {
        required: v => !!v || this.$i18n.t('default.requiredField')
      };
    },
    wmsLayers() {
      return this.$store.getters.getWMSLayers;
    }
  },
  methods: {
    confirmSuccess() {
      if (this.$refs.form.validate()) {
        const payload = {
          layer_type: 'webservice',
          name_pl: this.wmsData.name_pl,
          name_en: this.wmsData.name_en,
          wms_url: this.wmsData.wms_url,
          wms_layers: this.wmsData.wms_layers.join(','),
          wms_format: this.wmsData.wms_format,
          wms_srs: this.wmsData.wms_srs
        };
        this.$store.dispatch('addLayer', payload).then(r => {
          if (r.status === 201) {
            this.$root.$emit(
              'showSnackbar',
              this.$i18n.t(`settings.addSuccess`)
            );
            this.$emit('success');
            // delete payload.layer_type;
            payload.id = r.data.layers;
            this.$store.commit('setWmsList', this.wmsLayers.concat(payload));
          } else {
            //
          }
        });
      }
    },
    cancelDelete() {
      this.$emit('cancel');
    },
    async downloadLayers() {
      if (this.wmsData.wms_url) {
        this.downloadingCapabilities = true;
        try {
          const r = await this.$store.dispatch(
            'getWMSCapabilities',
            this.wmsData.wms_url
          );
          if (r.status === 200) {
            let result;
            try {
              result = new WMSCapabilities().read(r.data);
            } catch (e) {
              this.error = true;
              return false;
            }
            this.isParsed = true;
            if (result.Capability.Layer.hasOwnProperty('Layer')) {
              this.layers = result.Capability.Layer.Layer;
            } else {
              this.layers = [result.Capability.Layer];
            }
            this.formats = result.Capability.Request.GetMap.Format;
            this.coordsSystems = result.Capability.Layer.CRS;
          } else {
            this.error = true;
          }
        } catch (e) {
          this.error = true;
          this.isParsed = false;
        }
        this.downloadingCapabilities = false;
      }
    }
  }
};
</script>

<style lang="scss"></style>
